import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { scanForEvseWs } from 'src/Libs/flow/installation/api/ws/scan-for-evse-ws'
import { Device } from 'src/Libs/flow/installation/forms'
import { logError, logMessage } from 'src/Libs/flow/utils'

type EebusResult = {
  shipId: string
  ski: string
  eebusId: string
}

interface EvseScanModel {
  media: string
  results: {
    firstScanComplete: boolean
    isPartialResult: boolean
    devices: EebusResult[]
  }
}

//#region Editor
interface EebusPairingEditorProps {
  installationId: number
  emcId: string
  evse: Device
  setPairingId: (pairingId: string) => void
  currentPairingId: string | undefined
}

/** Dialog content for EEBus pairing on evse charging stations */
export const EebusPairingEditor: React.FC<EebusPairingEditorProps> = ({ installationId, emcId, evse, setPairingId, currentPairingId }) => {
  const { t } = useCustomTranslation()

  const [error, setError] = useState<{ code: number; message: string } | null>(null)
  const [data, setData] = useState<EvseScanModel | null>(null)

  const isLoadingFirstScan = data?.results?.devices?.length === 0 && !data?.results?.firstScanComplete
  const isLoading = (!data && !error) || isLoadingFirstScan

  const deviceId = evse.deviceId

  const { authToken } = useContext(AuthContext)

  useEffect(() => {
    if (!authToken) return

    const evseWebSocket = scanForEvseWs(installationId, emcId, deviceId, authToken)

    evseWebSocket.onopen = () => logMessage('Evse pairing socket: connected')
    evseWebSocket.onclose = () => logMessage('Evse pairing socket: disconnected')
    evseWebSocket.onerror = e => logError('Evse pairing socket error:', e)

    evseWebSocket.onmessage = e => {
      const eventData = JSON.parse(e.data)

      if (eventData.statusCode === 204 && Object.keys(eventData).length === 1) {
        setData(null)
        return
      }

      if (eventData.statusCode === 408) {
        setData(null)
        setError({ code: eventData.statusCode, message: eventData.error })
        return
      }

      if (eventData.statusCode === 400) {
        // Unknown connection error, so display last successful result + errorMessage
        setError({
          code: eventData.statusCode,
          message: eventData.error ?? t('installation.productEditor.rscpError'),
        })
        return
      }

      // Here, all the errors are already handled. This message contains data.
      setData(eventData)
    }

    return () => {
      evseWebSocket.close()
    }
  }, [installationId, emcId, deviceId, t, authToken])

  if (error) {
    return <Typography color="error">{error.message}</Typography>
  }

  if (isLoading || !data) {
    return (
      <Box textAlign={'center'} pt={1}>
        <CircularProgress />
      </Box>
    )
  }

  if (!data?.results?.devices?.length) {
    return <Alert severity="error" text={t('installation.productEditor.scanEvses.noResults')} />
  }

  return (
    <Box maxHeight={500}>
      {data.results.devices.map(evseResult => {
        const pairingId = evseResult.ski

        return (
          <NetworkScanResultEvse
            key={pairingId}
            evse={evseResult}
            handleSelect={() => setPairingId(pairingId)}
            isSelected={currentPairingId === pairingId}
          />
        )
      })}
    </Box>
  )
}
//#endregion

//#region NetworkScan results
interface NetworkScanResultEvseProps {
  evse: EebusResult
  handleSelect: () => void
  isSelected: boolean
}

const NetworkScanResultEvse: React.FC<NetworkScanResultEvseProps> = ({ evse, handleSelect, isSelected }) => {
  const { t } = useCustomTranslation()

  const name = evse.shipId
  const pairingId = evse.ski

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: theme => `1px solid ${theme.palette.grey[500]}`,
        py: 2,
        flexDirection: { xs: 'column', sm: 'initial' },
        alignItems: { xs: 'normal', sm: 'center' },
      }}
    >
      <Box>
        <Box display={'flex'}>
          <Title>{t('common.name')}</Title>
          <PairingId>{name}</PairingId>
        </Box>
        <Box display={'flex'}>
          <Title>{t('installation.productEditor.scanNetwork.ski')}</Title>
          <PairingId>{pairingId}</PairingId>
        </Box>
      </Box>
      <Button color={'secondary'} variant={'outlined'} size="large" onClick={handleSelect} disabled={isSelected}>
        {isSelected ? t('installation.productEditor.scanNetwork.selected') : t('installation.productEditor.scanNetwork.select')}
      </Button>
    </Box>
  )
}

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ minWidth: { xs: 60, sm: 100, lineHeight: 1.5 } }}>{children}</Typography>
)

const PairingId: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 'bold',
      lineHeight: 1.5,
      maxWidth: { xs: '80%', sm: undefined },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {children}
  </Typography>
)
//#endregion
